import { Alert, CircularProgress } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import { useNotification } from "@/shared/components/notifications/useNotification";
import {
  filterToCondition,
  type ActiveFiltersState,
  type Filter,
  type FilterMatchMode,
  type FilterMenuOutput,
} from "@/shared/components/ResourceView";
import { TargetEntityType } from "@/shared/domain/customFields";
import { useReadyCustomFields } from "@/shared/domain/customFields/hooks";
import { type DashboardRecord } from "@/shared/domain/dashboards";
import {
  dashboardQueryKeys,
  useDashboard,
  useDeleteDashboard,
} from "@/shared/domain/dashboards/hooks";
import { useCheckFeatureFlag } from "@/shared/domain/featureFlags/hooks";
import { Period } from "@/shared/domain/metrics";
import { useAllMetricDefinitions } from "@/shared/domain/metrics/hooks";
import { Button } from "@/shared/foundation/primitives";
import { type Updater } from "@/shared/foundation/primitives/Table";
import { RobotoDomainException } from "@/shared/http";
import { Goto } from "@/shared/navigation/Goto";

import { CardEditPopover } from "../CardEditPopover/CardEditPopover";
import { DashboardConflictDialog } from "../dashboardConflict/DashboardConflictDialog";
import {
  clearDraft,
  draftKeyFor,
  loadDraft,
  saveDraft,
  sweepExpiredDrafts,
} from "../dashboardDrafts/dashboardDraft";
import { DashboardLeaveGuard } from "../dashboardDrafts/DashboardLeaveGuard";
import { planDraftRestore } from "../dashboardDrafts/draftRestore";
import { DraftRestoreDialog } from "../dashboardDrafts/DraftRestoreDialog";
import { DashboardFilterBar } from "../DashboardFilterBar/DashboardFilterBar";
import {
  buildDashboardCustomFieldFilterDefinitions,
  DASHBOARD_PROPERTY_FILTERS,
  sortByFieldToken,
} from "../DashboardFilterBar/dashboardPropertyFilters";
import { DashboardHeader } from "../DashboardHeader/DashboardHeader";
import {
  appendFilter,
  applyDefinitionTimeParams,
  buildDashboardDefinition,
  CardConfig,
  clearFilters,
  DASHBOARD_PARAMS,
  DashboardDefinition,
  DashboardLayout,
  definitionsDifferInLayout,
  effectiveChartMetric,
  effectiveTrendMetric,
  emptyDashboardLayout,
  isDashboardDefinition,
  mergeParams,
  normalizeDashboardId,
  normalizeDefinition,
  periodFitsRange,
  periodForRange,
  rangeForPeriod,
  readFilterState,
  readPeriod,
  readRange,
  removeFilterAt,
  setFilterMatchMode,
  TimeRange,
  timeRangeToNsBounds,
  updateFilterAt,
} from "../DashboardHeader/DashboardParams";
import {
  DashboardSections,
  LayoutChange,
} from "../DashboardSections/DashboardSections";
import { MetricChartCard } from "../MetricChartCard/MetricChartCard";
import { MetricsOnboarding } from "../MetricsOnboarding/MetricsOnboarding";
import { TrendTile } from "../TrendTile/TrendTile";

import {
  clearRememberedDashboardId,
  loadRememberedDashboardId,
  rememberDashboardId,
} from "./dashboardSelection";
import { defaultDashboardLayout } from "./dashboardShowcase";
import styles from "./MetricsDashboard.module.css";
import {
  type ExplicitDashboardSelection,
  useInitialDashboardSelection,
} from "./useInitialDashboardSelection";

/** Matches the app's error-toast duration for dialog-less mutation failures
 * (see DashboardMenu's header-Save). */
const ERROR_NOTIFICATION_DURATION_MS = 6000;

/**
 * String message for `showNotification`, which only accepts strings. A backend
 * 4XX message is server-authored and safe to show verbatim; anything else gets
 * the operation-specific fallback. (Twin of DashboardMenu's helper.)
 */
function toastErrorMessage(error: unknown, fallback: string): string {
  return error instanceof RobotoDomainException && error.isClientError()
    ? error.message
    : fallback;
}

export interface MetricsDashboardProps {
  orgId: string | undefined;
  /** Restrict every query to one device. Top-level dashboard omits this. */
  deviceId?: string;
  /** Restrict every query to one session. Top-level dashboard omits this. */
  sessionId?: string;
}

/**
 * Org-, device-, or session-scoped metrics dashboard. The same component
 * powers (a) the top-level metrics route (no filter), (b) the device-detail
 * "Metrics" tab (deviceId set), and (c) any session-scoped view (sessionId
 * set).
 *
 * Layout, top to bottom: global controls (time range + period) → the placed
 * card grid ({@link DashboardSections}): titled sections, each a 6-column
 * canvas where cards (trend rows and charts) carry explicit placements and
 * can be dragged, resized, added at any empty cell, and removed.
 *
 * View-state ownership: the URL carries only the top-level configuration —
 * the active dashboard id as the `:dashboardId?` path param, `range` and
 * `period` as query params; the layout itself
 * (sections + cards) is component state. Every layout edit snapshots the
 * full definition into a per-context localStorage draft
 * (`dashboardDraft.ts`) so unsaved edits survive reload, and Save persists
 * it as a versioned `dashboard_definition` on the backend record. When the org has no metric
 * definitions the whole thing is replaced by an onboarding empty state.
 *
 * This component never renders the metric-definitions *management* table —
 * that lives behind the admin-only "Metric Definitions" tab in
 * `MetricsContent`.
 */
export function MetricsDashboard({
  orgId,
  deviceId,
  sessionId,
}: MetricsDashboardProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const { dashboardId: dashboardIdParam } = useParams<{
    dashboardId?: string;
  }>();
  const range = readRange(searchParams);
  const period = readPeriod(searchParams, range);

  // The layout (sections + placed cards) as component state — never URL
  // params, so layout edits cause no URL churn. (The initial value is never
  // painted: the seeding effect below replaces it before the canvas renders.)
  const [layout, setLayout] = useState<DashboardLayout>(emptyDashboardLayout);
  const { sections, cards } = layout;

  // The card whose "Edit Data" popover is open, anchored to its ⋮ menu button.
  const [editing, setEditing] = useState<{
    cardId: string;
    anchorEl: HTMLElement;
  } | null>(null);

  // Normalized at the read site: an absent or empty/whitespace `:dashboardId?`
  // path segment is `null` here, so the render gate, `useDashboard`, and draft
  // keys all agree on what "no dashboard selected" means.
  const activeDashboardId = normalizeDashboardId(dashboardIdParam);
  const dashboardQuery = useDashboard(activeDashboardId);
  const loadedDashboard = dashboardQuery.data ?? null;

  // A loaded record whose stored definition is not a valid current-schema
  // layout must never seed (or masquerade as) the dashboard. Like a hard-failed
  // fetch, it can never yield a usable layout, so it feeds the same failure
  // signal below rather than dead-ending the page.
  const loadedDefinitionInvalid =
    loadedDashboard !== null &&
    loadedDashboard.dashboard_id === activeDashboardId &&
    !isDashboardDefinition(loadedDashboard.dashboard_definition);

  // A selected dashboard that can never load — its fetch hard-failed (e.g. a
  // non-existent id that 404s) or its stored definition is invalid — must not
  // dead-end the whole page. We treat it as if no dashboard is effectively
  // active: the default/empty view seeds and renders normally (under an error
  // banner surfaced below), and — crucially — its draft keys under the
  // UNSAVED-view context, so edits are never persisted under the failed
  // dashboard's key. The picker stays present as the recovery affordance.
  const dashboardLoadFailed =
    activeDashboardId !== null &&
    (dashboardQuery.isError || loadedDefinitionInvalid);
  const effectiveDashboardId = dashboardLoadFailed ? null : activeDashboardId;

  const {
    data: definitions,
    isLoading,
    error,
  } = useAllMetricDefinitions(orgId);

  // Which layout an UNSAVED view seeds. "empty" (one bare section, no cards)
  // is the default — the "New dashboard" action, a resumed unsaved draft,
  // and the failed-load fallback all seed it. "showcase" (the full
  // Dashboard Showcase seed) is set ONLY by a priority-3 landing outcome:
  // the org has metrics but not a single visible dashboard.
  const [unsavedSeed, setUnsavedSeed] = useState<"empty" | "showcase">("empty");

  // The user's explicit in-session choice, readable synchronously by the
  // landing-selection hook at navigation time: `unsaved-view` marks a bare
  // route the user chose on purpose ("New dashboard", the post-delete
  // fallback), which re-entry must not re-resolve away from. Written by the
  // menu/save handlers below.
  const explicitSelectionRef = useRef<ExplicitDashboardSelection>({
    kind: "none",
  });

  // Landing-time selection: on each navigation onto a bare route, pick the
  // remembered/default dashboard (navigating to it with `replace`), the
  // unsaved draft, or the showcase — see useInitialDashboardSelection. While
  // it decides, the render below shows a spinner so the wrong canvas is
  // never painted.
  const { deciding: selectionDeciding } = useInitialDashboardSelection({
    orgId,
    deviceId,
    activeDashboardId,
    // The heuristic serves the two routed mounts (org page, device tab).
    enabled: sessionId === undefined,
    explicitSelectionRef,
    definitionsCount: definitions?.length,
    onShowcase: () => setUnsavedSeed("showcase"),
  });

  const draftKey = draftKeyFor({
    orgId,
    deviceId,
    sessionId,
    dashboardId: effectiveDashboardId,
  });

  // Once per mount, age out abandoned drafts everywhere (all orgs/scopes) —
  // the lazy per-key purge in loadDraft only ever touches keys the user
  // revisits.
  useEffect(() => {
    sweepExpiredDrafts();
  }, []);

  // A stored draft that differs from what this context would load without it
  // (the saved record, or the default layout). The baseline is already
  // seeded and renders beneath a modal dialog asking whether to restore the
  // draft; keyed so a stale prompt can never apply across a context switch.
  const [pendingDraft, setPendingDraft] = useState<{
    key: string;
    draft: DashboardDefinition;
  } | null>(null);

  // The revision the canvas was seeded from — the "this is what I loaded" half
  // of the save contract, held for the whole editing session.
  //
  // Deliberately NOT read from a query cache at save time. `useDashboards` polls
  // every 60s and `useDashboard` inherits the global 5-minute staleTime with
  // refetchOnWindowFocus enabled, so a cache read at Save would quietly adopt a
  // concurrent writer's revision and wave through the very save the guard exists
  // to stop. Keyed like `pendingDraft` so a capture can only apply to the
  // context it was made for.
  const [baseRevision, setBaseRevision] = useState<{
    key: string;
    revision: number;
  } | null>(null);
  const effectiveBaseRevision =
    baseRevision?.key === draftKey ? baseRevision.revision : null;

  // A save was rejected because someone else changed the dashboard first. Held
  // here rather than in the save surfaces because both resolutions act on state
  // this component owns — the layout, the URL params, the draft, the base
  // revision, and the dashboard selection. `fromLeaveGuard` records which
  // surface raised it, so a conflict raised mid-navigation can release the
  // blocked navigation once it is resolved.
  const [conflict, setConflict] = useState<{
    serverRecord: DashboardRecord;
    fromLeaveGuard: boolean;
  } | null>(null);

  // Set synchronously before navigating after "save a copy" so the leave
  // guard's blocker sees it immediately (refs update synchronously, unlike
  // state). The edits ARE persisted — just under a different dashboard id —
  // so the guard has no data-loss concern to raise. Cleared on the next
  // render cycle once the new dashboard seeds the canvas.
  const savedElsewhereRef = useRef(false);
  const queryClient = useQueryClient();

  // Seed the layout once per context (org / scope / dashboard id) from the
  // BASELINE — the loaded record, or the default seed layout for an unsaved
  // view. A stored draft no longer seeds silently: when it differs from the
  // baseline it becomes a pendingDraft (the restore prompt above); when it
  // normalizes identically it holds no real edit and is dropped. A record
  // baseline's range and period hydrate into the URL; from then on state
  // (layout) and URL (range/period) are the live sources of truth during
  // editing.
  const seededForKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (seededForKeyRef.current === draftKey) {
      return;
    }
    const draft = loadDraft(draftKey);
    // `revision` is the record's revision for a saved dashboard, or null for an
    // unsaved view (whose save path is a create, which cannot conflict).
    const seedBaseline = (
      baseline: DashboardDefinition,
      revision: number | null,
    ) => {
      seededForKeyRef.current = draftKey;
      savedElsewhereRef.current = false;
      setBaseRevision(revision === null ? null : { key: draftKey, revision });
      setLayout({ sections: baseline.sections, cards: baseline.cards });
      const plan = planDraftRestore(draft, baseline);
      if (plan.kind === "drop-draft") {
        clearDraft(draftKey);
      }
      setPendingDraft(
        plan.kind === "prompt" ? { key: draftKey, draft: plan.draft } : null,
      );
    };
    if (effectiveDashboardId === null) {
      // Unsaved view — or a hard-failed load treated as one. While the
      // landing selection is still deciding, don't seed: the outcome may be
      // a navigation to a saved dashboard, or the showcase seed kind.
      if (selectionDeciding) {
        return;
      }
      // The baseline is the seed layout for this unsaved view — the
      // Dashboard Showcase only on a priority-3 landing, the empty layout
      // everywhere else — under the URL's current range/period (no URL
      // writes, matching the draft-less path).
      const seed =
        unsavedSeed === "showcase"
          ? defaultDashboardLayout()
          : emptyDashboardLayout();
      seedBaseline(
        buildDashboardDefinition(searchParams, seed.sections, seed.cards),
        null,
      );
      return;
    }
    if (
      loadedDashboard !== null &&
      loadedDashboard.dashboard_id === effectiveDashboardId &&
      isDashboardDefinition(loadedDashboard.dashboard_definition)
    ) {
      const normalized = normalizeDefinition(
        loadedDashboard.dashboard_definition,
      );
      seedBaseline(normalized, loadedDashboard.revision);
      setSearchParams((prev) => applyDefinitionTimeParams(prev, normalized), {
        replace: true,
      });
    }
    // Otherwise the record is still loading (the effect re-runs on arrival;
    // a draft for a saved dashboard also waits — prompting needs the record
    // to compare against and to render as the baseline). An invalid stored
    // definition never reaches here: it flips effectiveDashboardId to null
    // above, so the default view seeds under the error banner.
  }, [
    draftKey,
    effectiveDashboardId,
    loadedDashboard,
    searchParams,
    setSearchParams,
    selectionDeciding,
    unsavedSeed,
  ]);

  // True once the current context's baseline (the loaded record, or the
  // default layout for an unsaved view) has seeded the layout state. Until
  // then the layout still holds the PREVIOUS context's (or the initial
  // seed's) content, so it must be neither rendered as this dashboard nor
  // snapshotted into this dashboard's draft. Reading the ref during render
  // is sound here: the seeding effect always pairs the ref write with a
  // setLayout, which re-renders with the updated value.
  const dashboardSeeded = seededForKeyRef.current === draftKey;

  // The restore prompt's two exits. Restoring applies the draft over the
  // already-seeded baseline (and keeps it stored — it still represents
  // unsaved edits); discarding keeps the baseline and clears the draft so it
  // doesn't resurrect on the next visit.
  const handleRestorePendingDraft = () => {
    if (pendingDraft === null || pendingDraft.key !== draftKey) {
      // The key check backs the invariant that a prompt can only ever apply
      // to the context it was created for.
      return;
    }
    const { draft } = pendingDraft;
    setLayout({ sections: draft.sections, cards: draft.cards });
    setSearchParams((prev) => applyDefinitionTimeParams(prev, draft), {
      replace: true,
    });
    // Re-save so restoring resets the draft's sliding TTL clock — choosing
    // to keep the edits is a touch, even before the next actual edit.
    saveDraft(draftKey, draft);
    setPendingDraft(null);
  };
  const handleDiscardPendingDraft = () => {
    if (pendingDraft === null) {
      return;
    }
    clearDraft(pendingDraft.key);
    setPendingDraft(null);
  };

  const currentDefinition = useMemo(
    () => buildDashboardDefinition(searchParams, sections, cards),
    [searchParams, sections, cards],
  );
  // Two tiers of "dirty" against the loaded record:
  //
  // - `isDirty` (SAVEABLE-dirty): ANY difference, range/period included.
  //   Drives the Save affordances and the "(unsaved changes)" marker
  //   (DashboardMenu) — a params-only tweak is still worth persisting.
  // - `isLayoutDirty` (BLOCKING-dirty): a sections/cards difference only.
  //   Drives the leave guard (navigation blocker + beforeunload) — a user
  //   who only tweaked range/period is never interrupted on the way out.
  const isDirty = useMemo(() => {
    if (
      loadedDashboard === null ||
      !isDashboardDefinition(loadedDashboard.dashboard_definition)
    ) {
      // No loaded record to be dirty against; an invalid stored definition
      // renders as a load error and never seeds, so nothing is comparable.
      return false;
    }
    // Both sides normalize through normalizeDefinition /
    // buildDashboardDefinition, so a stored record loads clean (JSONB key
    // order notwithstanding) and only real edits read as dirty.
    return (
      JSON.stringify(
        normalizeDefinition(loadedDashboard.dashboard_definition),
      ) !== JSON.stringify(currentDefinition)
    );
  }, [loadedDashboard, currentDefinition]);
  const isLayoutDirty = useMemo(() => {
    if (
      !isDirty ||
      loadedDashboard === null ||
      !isDashboardDefinition(loadedDashboard.dashboard_definition)
    ) {
      // Layout-dirty implies saveable-dirty, so a clean view short-circuits.
      return false;
    }
    return definitionsDifferInLayout(
      loadedDashboard.dashboard_definition,
      currentDefinition,
    );
  }, [isDirty, loadedDashboard, currentDefinition]);

  const handleDashboardMenu = (
    nextId: string | null,
    meta: { reason: "select" | "new" | "deleted" } = {
      reason: nextId === null ? "new" : "select",
    },
  ) => {
    if (nextId !== null) {
      // A menu load is one of the two explicit-choice recording sites (the
      // other is a successful save). Deep links and the landing heuristic's
      // automatic default never pass through here.
      explicitSelectionRef.current = { kind: "dashboard", dashboardId: nextId };
      if (orgId !== undefined) {
        rememberDashboardId(orgId, nextId);
      }
    } else {
      // "New dashboard" (an explicit discard of the unsaved-context draft)
      // and the post-delete fallback both reset the canvas to the EMPTY
      // layout — the showcase seed is reserved for the priority-3 landing.
      // Marking the context as seeded keeps the seeding effect from
      // resurrecting anything. Neither records a remembered choice; deleting
      // the currently-remembered dashboard clears the stored id (hygiene —
      // the resolver's staleness fallback would catch it anyway).
      if (
        meta.reason === "deleted" &&
        orgId !== undefined &&
        activeDashboardId !== null &&
        loadRememberedDashboardId(orgId) === activeDashboardId
      ) {
        clearRememberedDashboardId(orgId);
      }
      // The bare route is now the user's own destination — re-entering it
      // must keep this canvas, not re-resolve a landing selection.
      explicitSelectionRef.current = { kind: "unsaved-view" };
      const unsavedKey = draftKeyFor({
        orgId,
        deviceId,
        sessionId,
        dashboardId: null,
      });
      clearDraft(unsavedKey);
      seededForKeyRef.current = unsavedKey;
      setUnsavedSeed("empty");
      setLayout(emptyDashboardLayout());
    }
    // The dashboard id is a route path segment; swap it while carrying the
    // query string (range/period/tab) over. An explicit dashboard switch is
    // a real navigation, so it PUSHES a history entry (back returns to the
    // previous dashboard) — unlike the range/period controls, which tweak
    // the current view in place and use `replace`, and unlike the landing
    // heuristic's automatic default, which replaces so Back is never trapped
    // on the pre-redirect bare URL.
    navigate({
      pathname: Goto.createMetricsDashboardRoute({
        deviceId,
        dashboardId: nextId,
      }),
      search: searchParams.toString(),
    });
  };

  // After a successful Save / Save-as the backend record matches the live
  // view, so the draft is no longer an unsaved edit — drop it. (For Save-as
  // this fires before the switch to the new dashboard id, clearing the
  // context the draft was written under.) Saving is also the other
  // explicit-choice recording site: the saved record — the NEW record for
  // Save-as — becomes the remembered dashboard, even when it was reached via
  // a deep link that recorded nothing.
  const handleDashboardSaved = (saved: DashboardRecord) => {
    clearDraft(draftKey);
    explicitSelectionRef.current = {
      kind: "dashboard",
      dashboardId: saved.dashboard_id,
    };
    if (orgId !== undefined) {
      rememberDashboardId(orgId, saved.dashboard_id);
    }
    // The mutation response is the only safe source of the new revision. Key it
    // off the SAVED record's id rather than the ambient draftKey so Save-as
    // lands the capture under the context the app is about to switch into (this
    // fires before onSelect).
    setBaseRevision({
      key: draftKeyFor({
        orgId,
        deviceId,
        sessionId,
        dashboardId: saved.dashboard_id,
      }),
      revision: saved.revision,
    });
  };

  // The header's "restore last saved version" button: discard the live
  // edits and re-apply the loaded record's definition — the same
  // normalize + apply steps the seeding effect uses — then clear the draft
  // (the edits it held were just discarded, so nothing may resurrect on the
  // next visit). Only reachable while a dirty saved dashboard is loaded
  // (DashboardMenu gates the button on that), so the guard is
  // belt-and-braces for a stale click during a context switch.
  const handleDashboardRestore = () => {
    if (
      loadedDashboard === null ||
      !isDashboardDefinition(loadedDashboard.dashboard_definition)
    ) {
      return;
    }
    const restored = normalizeDefinition(loadedDashboard.dashboard_definition);
    setLayout({ sections: restored.sections, cards: restored.cards });
    setSearchParams((prev) => applyDefinitionTimeParams(prev, restored), {
      replace: true,
    });
    clearDraft(draftKey);
    // The one place reading the detail cache for a revision is correct: the user
    // is explicitly adopting whatever it currently holds as their new baseline,
    // and the definition and revision come from the same record, so they cannot
    // disagree. Everywhere else the base revision must come from the seed or a
    // save response — see the setBaseRevision declaration.
    setBaseRevision({ key: draftKey, revision: loadedDashboard.revision });
  };

  // Conflict resolution 1: adopt the server's version and drop the local edits.
  // Re-seeds from the record carried by the conflict response rather than
  // refetching — that record is the row the server read under its write lock, so
  // its definition and revision are guaranteed mutually consistent, whereas a
  // refetch could land after yet another writer and re-open the same problem one
  // step later.
  const handleConflictDiscardAndReload = (serverRecord: DashboardRecord) => {
    if (isDashboardDefinition(serverRecord.dashboard_definition)) {
      const reloaded = normalizeDefinition(serverRecord.dashboard_definition);
      setLayout({ sections: reloaded.sections, cards: reloaded.cards });
      setSearchParams((prev) => applyDefinitionTimeParams(prev, reloaded), {
        replace: true,
      });
    }
    clearDraft(draftKey);
    setBaseRevision({ key: draftKey, revision: serverRecord.revision });
    // Keep the rest of the UI honest about what is now loaded.
    queryClient.setQueryData(
      dashboardQueryKeys.id(serverRecord.dashboard_id, serverRecord.org_id),
      serverRecord,
    );
    setConflict(null);
  };

  // Conflict resolution 2: the edits were persisted as a new dashboard, which
  // cannot conflict (it is a create). From the header we switch to the copy, as
  // Save-as does; from the leave guard the user asked to leave, so we let the
  // held navigation proceed instead of moving them to a different dashboard.
  const handleConflictSavedCopy = (created: DashboardRecord) => {
    const fromLeaveGuard = conflict?.fromLeaveGuard ?? false;
    handleDashboardSaved(created);
    setConflict(null);
    if (fromLeaveGuard) {
      showNotification(
        `Your changes were saved as "${created.name}".`,
        "success",
      );
      return;
    }
    // The edits are persisted under the new dashboard — tell the leave guard
    // there's nothing unsaved so it doesn't block the switch navigation.
    savedElsewhereRef.current = true;
    handleDashboardMenu(created.dashboard_id, { reason: "select" });
  };

  // Recovery affordance for the invalid-definition banner (below): a real
  // record exists but can never load, so we let the user delete it in place.
  // Only wired up for that case — a hard-failed fetch (404) has no record to
  // delete. On success we drop back to the unsaved/default view exactly as the
  // picker does (`handleDashboardMenu(null)`), which navigates to the bare
  // route (no dashboard path segment) so the banner disappears;
  // `useDeleteDashboard` invalidates the list cache.
  const showNotification = useNotification();
  const deleteMutation = useDeleteDashboard();
  const handleDeleteFailedDashboard = () => {
    if (activeDashboardId === null) {
      return;
    }
    deleteMutation.mutate(activeDashboardId, {
      onSuccess: () => handleDashboardMenu(null, { reason: "deleted" }),
      onError: (deleteError) => {
        // No dialog is on screen, so a failure surfaces as an error toast
        // (the app's pattern for dialog-less mutation failures).
        showNotification(
          toastErrorMessage(
            deleteError,
            "Failed to delete dashboard. Please try again.",
          ),
          "error",
          ERROR_NOTIFICATION_DURATION_MS,
        );
      },
    });
  };

  // Draft snapshots only ever persist AFTER the context has seeded: during a
  // dashboard's load window the layout still shows the previous/seed content,
  // and snapshotting it would store the wrong definition under the loading
  // dashboard's draft key. (The edit surfaces aren't rendered during the load
  // window either — this guards the persistence path itself.)
  const persistDraft = (definition: DashboardDefinition) => {
    if (seededForKeyRef.current !== draftKey) {
      return;
    }
    saveDraft(draftKey, definition);
  };

  // Params-only counterpart of persistDraft: a range/period tweak never
  // CREATES a draft — it is not a layout edit, its state lives in the URL,
  // and a draft holding nothing else would be dropped un-prompted on the
  // next visit anyway (planDraftRestore compares layout only). But when a
  // draft already exists (a real layout edit is outstanding), the tweak
  // refreshes it, so restoring the draft later brings back the params the
  // user last saw alongside their layout edit.
  const refreshDraftParams = (definition: DashboardDefinition) => {
    if (seededForKeyRef.current !== draftKey) {
      return;
    }
    if (loadDraft(draftKey) === null) {
      return;
    }
    saveDraft(draftKey, definition);
  };

  // Range and period auto-fit each other, but only when the current pair no
  // longer fits (one too small for the other) — a still-valid combination is
  // left as the user set it. Both live in the URL (replace, so controls don't
  // pollute history); an existing draft is refreshed with them (see
  // refreshDraftParams — a params-only change never creates one).
  const handleRangeChange = (next: TimeRange) => {
    const nextPeriod = periodFitsRange(next, period)
      ? period
      : periodForRange(next);
    const nextParams = mergeParams(searchParams, {
      [DASHBOARD_PARAMS.range]: next,
      [DASHBOARD_PARAMS.period]: nextPeriod,
    });
    setSearchParams(nextParams, { replace: true });
    refreshDraftParams(buildDashboardDefinition(nextParams, sections, cards));
  };

  const handlePeriodChange = (next: Period) => {
    const nextRange = periodFitsRange(range, next)
      ? range
      : rangeForPeriod(next);
    const nextParams = mergeParams(searchParams, {
      [DASHBOARD_PARAMS.period]: next,
      [DASHBOARD_PARAMS.range]: nextRange,
    });
    setSearchParams(nextParams, { replace: true });
    refreshDraftParams(buildDashboardDefinition(nextParams, sections, cards));
  };

  // Every layout edit (placement saves, add/remove card, section changes) is
  // one state update — no URL writes — snapshotted to the draft so a single
  // gesture never leaves a half-applied layout behind a reload.
  const handleLayoutChange = (next: LayoutChange) => {
    const nextLayout: DashboardLayout = {
      sections: next.sections ?? sections,
      cards: next.cards ?? cards,
    };
    setLayout(nextLayout);
    persistDraft(
      buildDashboardDefinition(
        searchParams,
        nextLayout.sections,
        nextLayout.cards,
      ),
    );
  };

  const updateCard = (id: string, next: CardConfig) => {
    handleLayoutChange({
      cards: cards.map((c) => (c.id === id ? next : c)),
    });
  };

  // Anchor the window to a stable `now` per range selection. Recomputing
  // `timeRangeToNsBounds` on every render would shift the ns bounds (and thus
  // every aggregate query key) each time the dashboard re-renders — e.g. when
  // opening the drill-down dialog — causing the backing tiles/chart to refetch.
  const bounds = useMemo(() => timeRangeToNsBounds(range), [range]);
  const deviceIds = deviceId ? [deviceId] : undefined;
  const sessionIds = sessionId ? [sessionId] : undefined;

  const customFieldsEnabled = useCheckFeatureFlag("release-custom-fields");
  const readyCollectionCustomFields = useReadyCustomFields({
    entityType: TargetEntityType.Collection,
    orgId,
    enabled: customFieldsEnabled,
  });
  const readyDeviceCustomFields = useReadyCustomFields({
    entityType: TargetEntityType.Device,
    orgId,
    enabled: customFieldsEnabled,
  });
  const readySessionCustomFields = useReadyCustomFields({
    entityType: TargetEntityType.Session,
    orgId,
    enabled: customFieldsEnabled,
  });
  const filterDefinitions = useMemo(
    () =>
      // Sorting the merged list interleaves the custom fields into their
      // entity's alphabetical block instead of trailing the natives.
      sortByFieldToken([
        ...DASHBOARD_PROPERTY_FILTERS,
        ...(customFieldsEnabled
          ? [
              ...buildDashboardCustomFieldFilterDefinitions(
                readyCollectionCustomFields.data ?? [],
                "collection",
              ),
              ...buildDashboardCustomFieldFilterDefinitions(
                readyDeviceCustomFields.data ?? [],
                "device",
              ),
              ...buildDashboardCustomFieldFilterDefinitions(
                readySessionCustomFields.data ?? [],
                "session",
              ),
            ]
          : []),
      ]),
    [
      customFieldsEnabled,
      readyCollectionCustomFields.data,
      readyDeviceCustomFields.data,
      readySessionCustomFields.data,
    ],
  );

  // Filter state lives in the `filters` search param, parsed once per URL
  // change (the memo below) so the parse + validation don't run every render.
  // The mutator callbacks read the live params fresh instead of closing over
  // `filters`, so a burst of edits never races a stale closure.
  const { filters, matchMode } = useMemo(
    () => readFilterState(searchParams),
    [searchParams],
  );
  const activeFilters = useMemo<ActiveFiltersState>(
    () =>
      filters.length > 0
        ? { type: "structured", filters, matchMode }
        : { type: "none" },
    [filters, matchMode],
  );
  const condition = useMemo(
    () =>
      filters.length > 0
        ? filterToCondition(filters, { matchMode })
        : undefined,
    [filters, matchMode],
  );

  const handleFilterSelected = (output: FilterMenuOutput) => {
    // Structured-only here (no RoboQL), but the callback's union type still
    // forces this guard.
    if (output.type === "structured") {
      setSearchParams((p) => appendFilter(p, output.filter), { replace: true });
    }
  };
  const handleFilterRemove = (index: number) =>
    setSearchParams((p) => removeFilterAt(p, index), { replace: true });
  const handleFilterUpdate = (index: number, filter: Filter) =>
    setSearchParams((p) => updateFilterAt(p, index, filter), { replace: true });
  const handleFiltersClear = () =>
    setSearchParams((p) => clearFilters(p), { replace: true });
  const handleMatchModeChange = (updater: Updater<FilterMatchMode>) => {
    const nextMode =
      typeof updater === "function" ? updater(matchMode) : updater;
    setSearchParams((p) => setFilterMatchMode(p, nextMode), { replace: true });
  };

  if (isLoading) {
    return (
      <div className={styles.state}>
        <CircularProgress size={28} />
      </div>
    );
  }

  if (error) {
    return (
      <Alert severity="error">Failed to load metrics: {error.message}</Alert>
    );
  }

  // The landing selection is still deciding what a bare route shows (waiting
  // on the dashboards list): keep the spinner up rather than flashing a
  // canvas that may immediately be replaced by the remembered/default
  // dashboard — the showcase invariant's visual half.
  if (selectionDeciding) {
    return (
      <div className={styles.state}>
        <CircularProgress size={28} />
      </div>
    );
  }

  // A selected dashboard whose record hasn't seeded the layout yet: render a
  // pending state instead of the editable canvas — until the record arrives
  // the layout state still holds the seed/previous content, which must never
  // masquerade as the requested dashboard. A load that can never succeed (a
  // hard-failed fetch or an invalid stored definition) is NOT gated here:
  // `effectiveDashboardId` is null for it, so it falls through to the normal
  // page + an error banner (below) rather than a full-page dead-end.
  if (effectiveDashboardId !== null && !dashboardSeeded) {
    return (
      <div className={styles.state}>
        <CircularProgress size={28} />
      </div>
    );
  }

  if (!definitions || definitions.length === 0) {
    return <MetricsOnboarding />;
  }

  const allNames = definitions.map((d) => d.name);

  // Seed indices for unresolved cards (trends AND charts, one shared
  // counter): the i-th card without a valid metric falls back to the i-th
  // definition, so a fresh dashboard shows as many distinct metrics as the
  // org has instead of repeating the first one (wrapping when there are more
  // cards than definitions).
  const seedIndexById = new Map<string, number>();
  {
    let seedIndex = 0;
    for (const card of cards) {
      if (!(card.metric !== null && allNames.includes(card.metric))) {
        seedIndexById.set(card.id, seedIndex);
        seedIndex += 1;
      }
    }
  }

  /** The metric a card actually shows: its own, or its resolved fallback. */
  const effectiveMetric = (card: CardConfig): string =>
    card.kind === "trend"
      ? effectiveTrendMetric(card, allNames, seedIndexById.get(card.id) ?? 0)
      : effectiveChartMetric(card, allNames, seedIndexById.get(card.id) ?? 0);

  const renderCardBody = (card: CardConfig) => {
    if (card.kind === "trend") {
      return (
        <TrendTile
          name={effectiveMetric(card)}
          aggregation={card.aggregation}
          orgId={orgId}
          period={period}
          startTimeNs={bounds.start_time_ns}
          endTimeNs={bounds.end_time_ns}
          deviceIds={deviceIds}
          sessionIds={sessionIds}
          condition={condition}
        />
      );
    }
    return (
      <MetricChartCard
        definitions={definitions}
        config={card}
        seedIndex={seedIndexById.get(card.id) ?? 0}
        orgId={orgId}
        period={period}
        startTimeNs={bounds.start_time_ns}
        endTimeNs={bounds.end_time_ns}
        deviceIds={deviceIds}
        sessionIds={sessionIds}
        condition={condition}
      />
    );
  };

  return (
    <div className={styles.page}>
      {/* A load that can never succeed — a hard-failed fetch (e.g. a
          non-existent dashboard id in the path) or a record whose stored
          definition is invalid — surfaces here as a banner over the normal page (the
          default/empty dashboard and the picker), instead of a full-page
          dead-end. The message reflects which failure occurred. */}
      {dashboardLoadFailed && (
        <Alert
          severity="error"
          action={
            // Only the invalid-definition case has a real record to delete; a
            // hard-failed fetch (e.g. a 404 id) has nothing to act on. The
            // dashboard is already broken and unloadable, so we delete directly
            // (no confirm) with pending/error feedback rather than gating a
            // known-broken record behind a dialog.
            loadedDefinitionInvalid ? (
              <Button
                variant="outlined"
                color="error"
                size="small"
                aria-label="Delete dashboard"
                onClick={handleDeleteFailedDashboard}
                disabled={deleteMutation.isPending}
                loading={deleteMutation.isPending}
              >
                {deleteMutation.isPending ? "Deleting…" : "Delete"}
              </Button>
            ) : undefined
          }
        >
          Failed to load dashboard:{" "}
          {loadedDefinitionInvalid
            ? "its saved definition is not a valid dashboard layout."
            : dashboardQuery.error?.message}
        </Alert>
      )}
      <DashboardHeader
        range={range}
        period={period}
        onRangeChange={handleRangeChange}
        onPeriodChange={handlePeriodChange}
        menu={{
          activeDashboardId: effectiveDashboardId,
          isDirty,
          currentDefinition,
          baseRevision: effectiveBaseRevision,
          onSelect: handleDashboardMenu,
          onSaved: handleDashboardSaved,
          onRevisionConflict: (serverRecord) =>
            setConflict({ serverRecord, fromLeaveGuard: false }),
          onRestore: handleDashboardRestore,
          // The unsaved view is the branded Dashboard Showcase only when the
          // priority-3 landing seeded it; an empty in-progress dashboard
          // keeps the generic placeholder.
          unsavedLabel:
            effectiveDashboardId === null && unsavedSeed === "showcase"
              ? "Dashboard Showcase"
              : undefined,
        }}
      />
      <DashboardFilterBar
        active={activeFilters}
        definitions={filterDefinitions}
        onFilterSelected={handleFilterSelected}
        onRemove={handleFilterRemove}
        onUpdate={handleFilterUpdate}
        onClear={handleFiltersClear}
        onMatchModeChange={handleMatchModeChange}
      />
      <DashboardSections
        sections={sections}
        cards={cards}
        onLayoutChange={handleLayoutChange}
        renderCardBody={renderCardBody}
        onEditCard={(card, anchorEl) =>
          setEditing({ cardId: card.id, anchorEl })
        }
      />
      {(() => {
        if (editing === null) {
          return null;
        }
        // Look the card up fresh each render so the popover tracks edits (and
        // closes itself if the card disappears, e.g. loading a dashboard).
        const card = cards.find((c) => c.id === editing.cardId);
        if (!card) {
          return null;
        }
        return (
          <CardEditPopover
            card={card}
            allNames={allNames}
            effectiveMetric={effectiveMetric(card)}
            anchorEl={editing.anchorEl}
            onClose={() => setEditing(null)}
            onChange={(next) => updateCard(card.id, next)}
          />
        );
      })()}
      {/* Holds in-app navigation (and arms beforeunload) while the loaded
          dashboard has unsaved LAYOUT edits (blocking-dirty) — a params-only
          range/period tweak is saveable but never blocks leaving. The
          unsaved view is never guarded: its edits persist as the
          localStorage draft and the restore prompt offers them back on the
          next visit. */}
      <DashboardLeaveGuard
        when={isLayoutDirty}
        savedElsewhereRef={savedElsewhereRef}
        dashboard={loadedDashboard}
        currentDefinition={currentDefinition}
        baseRevision={effectiveBaseRevision}
        onSaved={handleDashboardSaved}
        onRevisionConflict={(serverRecord) =>
          setConflict({ serverRecord, fromLeaveGuard: true })
        }
        onDiscard={() => clearDraft(draftKey)}
      />
      {conflict !== null && (
        <DashboardConflictDialog
          serverRecord={conflict.serverRecord}
          currentDefinition={currentDefinition}
          onDiscardAndReload={handleConflictDiscardAndReload}
          onSavedCopy={handleConflictSavedCopy}
        />
      )}
      {pendingDraft !== null && pendingDraft.key === draftKey && (
        <DraftRestoreDialog
          dashboardName={
            effectiveDashboardId !== null ? loadedDashboard?.name ?? null : null
          }
          onRestoreDraft={handleRestorePendingDraft}
          onDiscardDraft={handleDiscardPendingDraft}
        />
      )}
    </div>
  );
}
